
import React, { useMemo } from "react";
import { useLeads } from "@/context/LeadContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format, subDays, isAfter } from "date-fns";
import { el } from "date-fns/locale";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from "@/components/ui/chart";
import { BarChart2, MessageSquare, Clock, Eye, TrendingUp, Users } from "lucide-react";
import StatisticsCard from "@/components/dashboard/StatisticsCard";

const calculateDateRange = (days: number) => {
  const endDate = new Date();
  const startDate = subDays(endDate, days);
  return { startDate, endDate };
};

const MessageStatistics = () => {
  const { leads } = useLeads();

  const getMessagesPerDay = (days: number) => {
    const { startDate } = calculateDateRange(days);

    const dateLabels: string[] = [];
    const messagesPerDay: Record<string, number> = {};
    const viewsPerDay: Record<string, number> = {};

    for (let i = 0; i < days; i++) {
      const date = subDays(new Date(), i);
      const dateStr = format(date, "d MMM", { locale: el });
      dateLabels.unshift(dateStr);
      messagesPerDay[dateStr] = 0;
      viewsPerDay[dateStr] = 0;
    }

    leads.forEach(lead => {
      if (lead.smsStatus && new Date(lead.updatedAt) >= startDate) {
        const dateStr = format(new Date(lead.updatedAt), "d MMM", { locale: el });
        if (messagesPerDay[dateStr] !== undefined) {
          messagesPerDay[dateStr] += 1;
        }
      }

      if (lead.viewed && lead.viewedAt && new Date(lead.viewedAt) >= startDate) {
        const dateStr = format(new Date(lead.viewedAt), "d MMM", { locale: el });
        if (viewsPerDay[dateStr] !== undefined) {
          viewsPerDay[dateStr] += 1;
        }
      }
    });

    return dateLabels.map(date => ({
      date,
      messages: messagesPerDay[date] || 0,
      views: viewsPerDay[date] || 0
    }));
  };

  const partnerStats = useMemo(() => {
    const stats: Record<string, { partner: string, sent: number, viewed: number }> = {};

    leads.forEach(lead => {
      if (lead.smsStatus && lead.partnerName) {
        if (!stats[lead.partnerName]) {
          stats[lead.partnerName] = { partner: lead.partnerName.length > 12 ? lead.partnerName.substring(0, 12) + '...' : lead.partnerName, sent: 0, viewed: 0 };
        }
        stats[lead.partnerName].sent += 1;

        if (lead.viewed) {
          stats[lead.partnerName].viewed += 1;
        }
      }
    });

    return Object.values(stats).sort((a, b) => b.sent - a.sent);
  }, [leads]);

  const statusDistribution = useMemo(() => {
    const statuses = { sent: 0, delivered: 0, failed: 0, viewed: 0 };

    leads.forEach(lead => {
      if (lead.smsStatus === "sent") statuses.sent++;
      if (lead.smsStatus === "delivered") statuses.delivered++;
      if (lead.smsStatus === "failed") statuses.failed++;
      if (lead.viewed) statuses.viewed++;
    });

    return [
      { name: "Απεσταλμένα", value: statuses.sent, color: "#3b82f6" },
      { name: "Παραδόθηκαν", value: statuses.delivered, color: "#10b981" },
      { name: "Αποτυχία", value: statuses.failed, color: "#ef4444" },
      { name: "Αναγνώστηκαν", value: statuses.viewed, color: "#6366f1" }
    ];
  }, [leads]);

  const stats = useMemo(() => {
    const totalMessages = leads.filter(lead => lead.smsStatus).length;
    const viewedMessages = leads.filter(lead => lead.viewed).length;
    const viewRate = totalMessages > 0 ? Math.round((viewedMessages / totalMessages) * 100) : 0;

    const { startDate: thirtyDaysAgo } = calculateDateRange(30);
    const recentMessages = leads.filter(lead =>
      lead.smsStatus && isAfter(new Date(lead.updatedAt), thirtyDaysAgo)
    ).length;

    const { startDate: sixtyDaysAgo } = calculateDateRange(60);
    const previousPeriodMessages = leads.filter(lead =>
      lead.smsStatus &&
      isAfter(new Date(lead.updatedAt), sixtyDaysAgo) &&
      !isAfter(new Date(lead.updatedAt), thirtyDaysAgo)
    ).length;

    let trend = 0;
    let isPositive = true;
    if (previousPeriodMessages > 0) {
      const change = recentMessages - previousPeriodMessages;
      trend = Math.round((change / previousPeriodMessages) * 100);
      isPositive = change >= 0;
    }

    let avgViewTime = 0;
    let totalViewTime = 0;
    let viewsWithTime = 0;

    leads.forEach(lead => {
      if (lead.viewed && lead.viewedAt && lead.smsStatus && lead.updatedAt) {
        const sendTime = new Date(lead.updatedAt).getTime();
        const viewTime = new Date(lead.viewedAt).getTime();
        const timeDiff = (viewTime - sendTime) / (1000 * 60 * 60);

        if (timeDiff >= 0) {
          totalViewTime += timeDiff;
          viewsWithTime++;
        }
      }
    });

    if (viewsWithTime > 0) {
      avgViewTime = Math.round(totalViewTime / viewsWithTime * 10) / 10;
    }

    return { totalMessages, viewedMessages, viewRate, trend, isPositive, avgViewTime };
  }, [leads]);

  const chartConfig = {
    messages: { label: "Μηνύματα", theme: { light: "#3b82f6", dark: "#60a5fa" } },
    views: { label: "Αναγνώσεις", theme: { light: "#6366f1", dark: "#818cf8" } }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-lg">
          <MessageSquare className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Στατιστικά Μηνυμάτων</h1>
          <p className="text-slate-500 mt-0.5">Απόδοση SMS και αναγνώσεις</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatisticsCard
          title="Σύνολο Μηνυμάτων"
          value={stats.totalMessages}
          icon={<MessageSquare />}
          variant="info"
          trend={stats.trend !== 0 ? { value: stats.trend, isPositive: stats.isPositive } : undefined}
          description="Απεσταλμένα SMS"
        />

        <StatisticsCard
          title="Αναγνώσεις"
          value={stats.viewedMessages}
          icon={<Eye />}
          variant="success"
          description="Μηνύματα που διαβάστηκαν"
        />

        <StatisticsCard
          title="Ποσοστό Αναγνώσεων"
          value={`${stats.viewRate}%`}
          icon={<TrendingUp />}
          variant={stats.viewRate > 50 ? "success" : "warning"}
          description="Τελευταίες 30 ημέρες"
        />

        <StatisticsCard
          title="Μέσος Χρόνος"
          value={`${stats.avgViewTime} ώρες`}
          icon={<Clock />}
          variant="info"
          description="Από αποστολή σε ανάγνωση"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="daily">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="daily" className="rounded-md data-[state=active]:bg-white">Ημερήσια</TabsTrigger>
          <TabsTrigger value="partners" className="rounded-md data-[state=active]:bg-white">Ανά Συνεργάτη</TabsTrigger>
          <TabsTrigger value="status" className="rounded-md data-[state=active]:bg-white">Κατάσταση</TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-blue-50">
                  <BarChart2 className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Μηνύματα ανά Ημέρα</CardTitle>
                  <CardDescription>Τελευταίες 7 ημέρες</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-80">
              <ChartContainer config={chartConfig}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getMessagesPerDay(7)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar dataKey="messages" name="Μηνύματα" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="views" name="Αναγνώσεις" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="partners" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-emerald-50">
                  <Users className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Ανά Συνεργάτη</CardTitle>
                  <CardDescription>Αποστολές και αναγνώσεις</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={partnerStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="partner" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  <Legend />
                  <Bar dataKey="sent" name="Απεσταλμένα" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="viewed" name="Αναγνώσεις" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="status" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-violet-50">
                  <MessageSquare className="h-4 w-4 text-violet-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Κατάσταση Μηνυμάτων</CardTitle>
                  <CardDescription>Κατανομή ανά κατάσταση</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={3}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#94a3b8' }}
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MessageStatistics;
